/**
 * 签约前营销方案 · 默认负责人配置（2026-10-20 用户需求）。
 *
 * 需求原话：「在『签约前营销方案』模块内增加默认营销方案设计师配置，设计经理或管理员可维护；
 * 默认负责人优先取该配置；未配置时再取营销组组长或营销组首位成员；
 * 联系方式必须从员工档案读取姓名、电话、邮箱，不在项目中重复手填」。
 *
 * 实现要点
 * --------
 * 1. 配置值存 `settings.design_v3_presign_cfg.defaultDesigner`（username），
 *    与「团队分工」页的营销组 lead 是两个概念：组长是管理角色，默认负责人是实际做方案的人。
 * 2. 界面同时显示「当前生效的人」和「来源」：配置了什么就是什么；没配就显示回退到组长的结果，
 *    让经理一眼看出「我到底配没配」。
 * 3. 电话 / 邮箱只读展示，来自 users（回退 hrms_employees），不提供手填入口。
 */
import React, { useEffect, useState } from "react";
import { toast } from "../../../components/ui/sonner";
import { Button } from "../../../components/ui/button";
import { X, Loader2, UserCheck, Phone, Mail } from "lucide-react";
import { T } from "../../../lib/i18n";

export default function PresignSettingsPanel({ api, onClose, designers = [] }) {
  const [d, setD] = useState(null);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/design/presign/settings")
      .then(({ data }) => { setD(data); setVal(data.defaultDesigner || ""); })
      .catch(() => setD({ error: true }));
  }, [api]);

  // ESC 关闭：与项目其它手写弹窗一致（Radix dialog 的 z-50 会被项目抽屉压住，故沿用既有壳）
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/design/presign/settings", { defaultDesigner: val || null });
      setD((old) => ({ ...old, ...data }));
      toast.success(val ? T("默认营销方案设计师已保存") : T("已清除配置，将回退到营销组组长 / 首位成员"));
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  const cur = designers.find((x) => x.username === val);
  const dirty = (d?.defaultDesigner || "") !== val;

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && onClose()}
      data-testid="dv3-presign-settings">
      <div className="w-full max-w-lg space-y-3 rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-bold text-slate-900">{T("默认营销方案设计师")}</h3>
            <p className="mt-1 text-[11px] leading-5 text-slate-500">
              {T("销售下达营销方案任务时，系统优先带出这里配置的人；未配置时回退营销组组长，再回退营销组首位成员。")}
            </p>
          </div>
          <button type="button" aria-label={T("关闭")} onClick={onClose} data-testid="dv3-presign-settings-close"
            className="p-1 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>

        {!d ? <div className="h-24 animate-pulse rounded-lg bg-slate-100" />
          : d.error ? <p className="py-4 text-sm text-red-600">{T("配置加载失败，请重试")}</p>
            : (
              <>
                <div className="rounded-lg border border-slate-200 p-3" data-testid="dv3-presign-settings-current">
                  <p className="text-[11px] font-semibold text-slate-500">{T("当前生效")}</p>
                  <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                    <UserCheck className="h-4 w-4 text-emerald-600" />
                    {d.contact?.name || d.effectiveDesigner || "—"}
                    {!d.defaultDesigner && <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-normal text-amber-700">{T("回退（未配置）")}</span>}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{d.contact?.phone || T("未登记")}</span>
                    <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{d.contact?.email || T("未登记")}</span>
                  </div>
                </div>

                <label className="block text-[11px] font-semibold text-slate-600">
                  {T("指定默认负责人")}
                  <select value={val} onChange={(e) => setVal(e.target.value)} disabled={!d.isManager}
                    data-testid="dv3-presign-settings-select"
                    className="mt-1 w-full min-h-11 md:min-h-0 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm bg-white">
                    <option value="">{T("不指定（回退营销组组长 / 首位成员）")}</option>
                    {designers.map((x) => <option key={x.username} value={x.username}>{x.name}{x.phone ? ` · ${x.phone}` : ""}</option>)}
                  </select>
                </label>

                {cur && (
                  <div className="rounded-lg bg-slate-50 p-2.5 text-[11px] text-slate-600" data-testid="dv3-presign-settings-preview">
                    <p className="font-semibold text-slate-700">{T("项目负责人")}：{cur.name}</p>
                    <p>{T("联系电话")}：{cur.phone || T("未登记")}</p>
                    <p>{T("邮箱")}：{cur.email || T("未登记")}</p>
                  </div>
                )}

                <p className="text-[11px] text-slate-400">
                  {T("称呼、电话、邮箱均取自员工档案，修改员工资料后这里会自动更新。")}
                </p>

                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={onClose} disabled={busy}>{T("取消")}</Button>
                  <Button size="sm" data-testid="dv3-presign-settings-save" onClick={save} disabled={busy || !d.isManager || !dirty}
                    className="bg-slate-900 hover:bg-slate-700">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : T("保存")}
                  </Button>
                </div>
              </>
            )}
      </div>
    </div>
  );
}
